import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";

/**
 * Coordinates the mobile hero entrance choreography across the three
 * components that participate in it (HomeHero, Navbar, MobileStickyCta).
 *
 * Semantics:
 *  - "Fresh page load" = the tab session has never rendered the hero intro
 *    yet. Detected via a sessionStorage flag so a hard refresh replays it
 *    only when appropriate (session storage persists across refresh within
 *    the same tab; a new tab starts empty).
 *  - "Client-side back-navigation" = the user has already seen the intro
 *    during this SPA lifetime; the intro must NOT replay when they return
 *    to `/` via <Link>.
 *
 * Implementation:
 *  - `IS_FRESH_LOAD` is captured once at module import (page-load boundary).
 *    We flip the sessionStorage flag immediately so a mid-flight refresh
 *    behaves identically to any other refresh.
 *  - `heroHasMounted` starts false and is flipped to true on the FIRST hero
 *    mount, so any subsequent mounts (client-side back-nav) short-circuit
 *    even though `IS_FRESH_LOAD` is still true in memory.
 *  - Each consumer snapshots the answer in useState at first render, so all
 *    three components see the same coordinated value even if `heroHasMounted`
 *    flips between renders.
 */

const SESSION_KEY = "hero_intro_seen";

const IS_FRESH_LOAD: boolean = (() => {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(SESSION_KEY)) return false;
    window.sessionStorage.setItem(SESSION_KEY, "1");
    return true;
  } catch {
    return false;
  }
})();

let heroHasMounted = false;

/**
 * Returns `true` only during the initial choreography window on `/`.
 * Snapshotted per-component so late consumers still agree with early ones.
 */
export function useHeroIntro(): boolean {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  const [playIntro] = useState(
    () => IS_FRESH_LOAD && !heroHasMounted && pathname === "/" && !reduced,
  );
  return playIntro;
}

/**
 * Called from HomeHero once, on its first mount, to burn the intro flag so
 * subsequent client-side navigations back to `/` don't replay the sequence.
 */
export function useMarkHeroIntroConsumed(playing: boolean) {
  useEffect(() => {
    if (playing) heroHasMounted = true;
  }, [playing]);
}
